import { baseApi } from "./base";

/**
 * Notabene API endpoints for Travel Rule compliance
 */

// Types for Notabene API
export interface NotabeneTokenResponse {
  token: string;
  expiresIn: number;
  nodeUrl: string;
}

export interface CreateNotabeneTransferRequest {
  asset: string;
  amount: string;
  destinationAddress: string;
  memo?: string;
}

export interface NotabeneAgentDto {
  "@id": string;
  for: string;
  role?: string;
  policies?: NotabenePolicyDto[];
}

export interface NotabenePolicyDto {
  type: string;
  status?: string;
  data?: Record<string, unknown>;
}

export interface NotabeneTransferResponse {
  txId: string;
  status: string;
  agents?: NotabeneAgentDto[];
  policies?: NotabenePolicyDto[];
  counterparty?: Record<string, unknown>;
  errorMessage?: string;
  // Surfaced on the list endpoint so the resume hook can rebuild form
  // context (token / address / amount) when localStorage doesn't carry it.
  asset?: string;
  amount?: string;
  destinationAddress?: string;
  // Set on inbound transfers — used by the deposit-assist mount on the
  // ACTION_REQUIRED detail view (D13).
  sourceAddress?: string;
  // Direction lets the detail view switch copy between outbound + inbound.
  direction?: "OUTGOING" | "INCOMING";
  // D8 — true when Notabene's resolver returns a counterparty VASP that
  // disagrees with the user-declared destination. The Review stage shows
  // an acknowledgement checkbox in this case; backend forces TR_MANUAL_REVIEW
  // regardless.
  counterpartyMismatch?: boolean;
  // Optional human-readable reason from Notabene/compliance for the
  // current state (e.g. rejection reason, info-request prompt).
  reason?: string;
  // True when the originator still owes IVMS101 PII (Notabene requested a
  // presentation that hasn't been satisfied). The withdrawal modal keeps the
  // SafeConnect SDK mounted while this is set, even if the lifecycle status has
  // churned to a transient FLAGGED/PENDING (prod incident 2026-06-24).
  requiresPresentation?: boolean;
}

export interface NotabeneTransferAuthorizationResponse {
  authorized: boolean;
}

export interface VaspDirectoryRow {
  vaspDid: string;
  vaspName: string;
  jurisdiction?: string;
}

export interface AddressTrFieldsBody {
  isFirstParty?: boolean;
  thirdPartyRecipientName?: string;
  destinationType?: "Self-Hosted Wallet" | "Exchange" | "Other VASP";
  destinationVasp?: string;
  destinationVaspName?: string;
  walletVerificationReference?: string;
}

export interface DepositSenderInfoBody {
  senderFullName: string;
  senderCountryIso: string;
  selfOrThirdParty: "self" | "third_party";
  /**
   * Third-party sender's address (AU policy requires a full originator
   * address for an external sender; self branch uses KYC server-side).
   */
  senderStreetAddress?: string;
  senderTownName?: string;
  senderPostCode?: string;
  senderState?: string;
  /** Set when the customer's "self" branch completes the SafeConnect attestation. */
  walletVerificationReference?: string;
  /** IVMS101 payload returned by SafeConnect deposit-assist (self branch). */
  ivms101?: Record<string, unknown>;
  /**
   * SafeConnect ownership-proof blob (`{ proof }`) from deposit-assist — the
   * backend submits it upstream via Notabene's unhosted-proof PATCH.
   */
  confirmRelationship?: Record<string, unknown>;
}

// Inject Notabene endpoints into base API
const notabeneApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Get Notabene access token for SDK
    getNotabeneToken: build.query<NotabeneTokenResponse, void>({
      query: () => ({ url: `/notabene/token` }),
    }),

    // Create a new Notabene transfer (gated on the withdrawal-prepared token)
    createNotabeneTransfer: build.mutation<
      NotabeneTransferResponse,
      CreateNotabeneTransferRequest & { withdrawalPreparedToken?: string }
    >({
      query: ({ withdrawalPreparedToken, ...body }) => ({
        url: `/notabene/transfer`,
        method: "POST",
        body,
        headers: withdrawalPreparedToken
          ? { "X-Withdrawal-Prepared-Token": withdrawalPreparedToken }
          : undefined,
      }),
    }),

    // Get transfer status
    getNotabeneTransfer: build.query<NotabeneTransferResponse, string>({
      query: (txId) => ({ url: `/notabene/transfer/${txId}` }),
    }),

    completeNotabeneTransfer: build.mutation<
      NotabeneTransferResponse,
      {
        txId: string;
        ivms101?: Record<string, unknown>;
        confirmRelationship?: Record<string, unknown>;
        withdrawalPreparedToken?: string;
      }
    >({
      query: ({ txId, ivms101, confirmRelationship, withdrawalPreparedToken }) => ({
        url: `/notabene/transfer/${txId}/complete`,
        method: "POST",
        body: { ivms101, confirmRelationship },
        headers: withdrawalPreparedToken
          ? { "X-Withdrawal-Prepared-Token": withdrawalPreparedToken }
          : undefined,
      }),
    }),

    // Get all user transfers
    // The `customerId` arg is a CACHE KEY only — the URL never changes and
    // the server scopes the list to the authenticated profile. Keying the
    // cache per-profile stops one profile's cached list being served for
    // another after an account switch: with a void key, the hydrate
    // reconcile could run against the PREVIOUS profile's cached transfers
    // and write that profile's transfer straight back into the slice.
    // Callers must pass the active profile's customerId (skip until known).
    getNotabeneTransfers: build.query<NotabeneTransferResponse[], string>({
      query: () => ({ url: `/notabene/transfers` }),
      // Tagged so any mutation that creates / changes a transfer (the dev
      // simulator, sender-info submit, cancel-active) auto-refetches the
      // list — keeps the resume hook + DepositActionRequiredBanner in sync
      // without a page reload.
      providesTags: ["NotabeneTransfers"],
    }),

    // Check if transfer is authorized
    checkNotabeneTransferAuthorized: build.query<NotabeneTransferAuthorizationResponse, string>({
      query: (txId) => ({ url: `/notabene/transfer/${txId}/authorized` }),
    }),

    // VASP directory search (D3 Exchange branch)
    getVaspDirectory: build.query<VaspDirectoryRow[], string>({
      query: (q: string) => ({ url: `/notabene/vasps`, params: { q } }),
    }),

    // Travel-Rule address backfill PATCH (D4)
    updateAddressTrFields: build.mutation<
      void,
      {
        address: string;
        body: AddressTrFieldsBody;
      }
    >({
      query: ({ address, body }) => ({
        url: `/withdrawal/address/${encodeURIComponent(address)}/tr`,
        method: "PATCH",
        body,
      }),
    }),

    // Feature flag eval — frontend hook
    evalFeatureFlags: build.query<Record<string, boolean>, string[]>({
      query: (keys) => ({
        url: `/feature-flags/eval`,
        params: { keys: keys.join(",") },
      }),
    }),

    // User-initiated cancel of a single in-flight Travel Rule transfer.
    // Reserved for future per-row UI — the banner uses cancelActiveTransfers
    // (below) instead so it can clear sibling transfers in one shot.
    cancelNotabeneTransfer: build.mutation<void, string>({
      query: (txId) => ({
        url: `/notabene/transfer/${txId}/cancel`,
        method: "POST",
      }),
    }),

    // Cancel every still-active Travel Rule transfer for the current user.
    // Banner uses this so the user's "abandon" intent clears stranded
    // siblings (common after testing failures).
    cancelActiveNotabeneTransfers: build.mutation<void, void>({
      query: () => ({
        url: `/notabene/transfers/cancel-active`,
        method: "POST",
      }),
      invalidatesTags: ["NotabeneTransfers"],
    }),

    // D13 — submit sender information for an inbound deposit that's stuck
    // in ACTION_REQUIRED. Backend forwards the payload to Notabene `txUpdate`.
    submitDepositSenderInfo: build.mutation<
      void,
      { txId: string; body: DepositSenderInfoBody }
    >({
      query: ({ txId, body }) => ({
        url: `/transfers/${txId}/sender-info`,
        method: "POST",
        body,
      }),
      // Notabene's webhook ultimately drives the status change, but
      // invalidate optimistically so the banner clears as soon as the
      // submit succeeds — re-renders if Notabene rejects the update.
      invalidatesTags: ["NotabeneTransfers"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetNotabeneTokenQuery,
  useLazyGetNotabeneTokenQuery,
  useCreateNotabeneTransferMutation,
  useGetNotabeneTransferQuery,
  useLazyGetNotabeneTransferQuery,
  useCompleteNotabeneTransferMutation,
  useGetNotabeneTransfersQuery,
  useLazyGetNotabeneTransfersQuery,
  useCheckNotabeneTransferAuthorizedQuery,
  useLazyCheckNotabeneTransferAuthorizedQuery,
  useGetVaspDirectoryQuery,
  useLazyGetVaspDirectoryQuery,
  useUpdateAddressTrFieldsMutation,
  useEvalFeatureFlagsQuery,
  useLazyEvalFeatureFlagsQuery,
  useCancelNotabeneTransferMutation,
  useCancelActiveNotabeneTransfersMutation,
  useSubmitDepositSenderInfoMutation,
} = notabeneApi;

export { notabeneApi };
